import React from 'react';
import { 
  Box, 
  VStack, 
  HStack, 
  Grid,
  Skeleton,
  SkeletonText 
} from '@chakra-ui/react';

// Base skeleton with your design system colors
const BaseSkeleton = ({ children, ...props }) => (
  <Skeleton
    startColor="gray.800"
    endColor="gray.700"
    {...props}
  >
    {children}
  </Skeleton>
);

const BaseSkeletonText = ({ children, ...props }) => (
  <SkeletonText
    startColor="gray.800"
    endColor="gray.700"
    {...props}
  >
    {children}
  </SkeletonText>
);

// Engineers Page Skeletons
export const EngineerCardSkeleton = () => (
  <Box 
    bg="gray.900" 
    p={12} 
    border="1px solid" 
    borderColor="gray.700"
    minH="400px"
  >
    <VStack align="start" spacing={6}>
      {/* Avatar Skeleton */}
      <BaseSkeleton 
        w={20} 
        h={20} 
        borderRadius="md"
      />
      
      <VStack align="start" spacing={4} w="100%">
        <VStack align="start" spacing={1} w="100%">
          {/* Name Skeleton */}
          <BaseSkeleton h="24px" w="60%" />
          {/* Role Skeleton */}
          <BaseSkeleton h="16px" w="40%" />
        </VStack>
        
        {/* Bio Skeleton */}
        <BaseSkeletonText 
          noOfLines={4} 
          spacing={2}
          w="100%"
        />
        
        {/* Social Links Skeleton */}
        <VStack align="start" spacing={2} w="100%">
          <BaseSkeleton h="14px" w="20%" />
          <HStack spacing={4}>
            <BaseSkeleton h="14px" w="12%" />
            <BaseSkeleton h="14px" w="16%" />
            <BaseSkeleton h="14px" w="14%" />
          </HStack>
        </VStack>
      </VStack>
    </VStack>
  </Box>
);

export const EngineersPageSkeleton = () => (
  <Box py={20} px={4}>
    <VStack spacing={20} maxW="5xl" mx="auto">
      {/* Hero Section Skeleton */}
      <VStack spacing={8} textAlign="center" w="100%">
        <BaseSkeleton h="60px" w="600px" maxW="100%" />
        <BaseSkeletonText 
          noOfLines={3} 
          spacing={2}
          maxW="4xl"
        />
      </VStack>

      {/* Team Grid Skeleton */}
      <Grid templateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={8} w="100%">
        <EngineerCardSkeleton />
        <EngineerCardSkeleton />
        <EngineerCardSkeleton />
      </Grid>

      {/* Philosophy Section Skeleton */}
      <VStack spacing={8} w="100%" textAlign="center">
        <BaseSkeleton h="48px" w="400px" maxW="100%" />
        <BaseSkeletonText 
          noOfLines={3} 
          spacing={2}
          maxW="4xl"
        />
      </VStack>
    </VStack>
  </Box>
);

// Articles Page Skeletons
export const ArticleCardSkeleton = ({ featured = false }) => (
  <Box 
    bg="gray.900" 
    p={featured ? 12 : 8} 
    border="1px solid" 
    borderColor="gray.700"
    w="100%"
  >
    {featured ? (
      <Grid templateColumns="2fr 1fr" gap={12} alignItems="center">
        <VStack align="start" spacing={6}>
          <VStack align="start" spacing={3} w="100%">
            <HStack spacing={3}>
              <BaseSkeleton w={2} h={2} borderRadius="full" />
              <BaseSkeleton h="12px" w="120px" />
            </HStack>
            <BaseSkeleton h="32px" w="80%" />
            <BaseSkeletonText noOfLines={3} spacing={2} w="100%" />
            <BaseSkeleton h="12px" w="120px" />
          </VStack>
          
          <HStack spacing={4}>
            <BaseSkeleton h="32px" w="120px" borderRadius="md" />
            <BaseSkeleton h="12px" w="80px" />
          </HStack>
        </VStack>

        <BaseSkeleton h="200px" w="100%" />
      </Grid>
    ) : (
      <VStack align="start" spacing={6}>
        <VStack align="start" spacing={3} w="100%">
          <HStack spacing={2}>
            <BaseSkeleton w={1.5} h={1.5} borderRadius="full" />
            <BaseSkeleton h="10px" w="80px" />
          </HStack>
          <BaseSkeleton h="24px" w="90%" />
          <BaseSkeletonText noOfLines={3} spacing={2} w="100%" />
          <BaseSkeleton h="10px" w="100px" />
        </VStack>
        
        <HStack justify="space-between" w="100%">
          <BaseSkeleton h="12px" w="80px" />
          <BaseSkeleton h="10px" w="60px" />
        </HStack>
      </VStack>
    )}
  </Box>
);

export const ArticlesPageSkeleton = () => (
  <Box py={20} px={4}>
    <VStack spacing={20} maxW="5xl" mx="auto">
      {/* Hero Section Skeleton */}
      <VStack spacing={8} textAlign="center" w="100%">
        <BaseSkeleton h="60px" w="500px" maxW="100%" />
        <BaseSkeletonText 
          noOfLines={3} 
          spacing={2}
          maxW="4xl"
        />
      </VStack>

      {/* Featured Article Skeleton */}
      <ArticleCardSkeleton featured={true} />

      {/* Recent Articles Section */}
      <VStack spacing={12} w="100%">
        <BaseSkeleton h="48px" w="300px" maxW="100%" />
        
        <Grid templateColumns="repeat(2, 1fr)" gap={8}>
          <ArticleCardSkeleton />
          <ArticleCardSkeleton />
          <ArticleCardSkeleton />
          <ArticleCardSkeleton />
        </Grid>
      </VStack>

      {/* Categories Section */}
      <VStack spacing={8} w="100%">
        <BaseSkeleton h="48px" w="400px" maxW="100%" />
        
        <Grid templateColumns="repeat(auto-fit, minmax(120px, 1fr))" gap={4} maxW="3xl">
          {[...Array(6)].map((_, i) => (
            <BaseSkeleton key={i} h="32px" borderRadius="md" />
          ))}
        </Grid>
      </VStack>
    </VStack>
  </Box>
);

// Projects Page Skeletons
export const ProjectCategorySkeleton = () => (
  <Box bg="gray.900" p={8} border="1px solid" borderColor="gray.700">
    <VStack align="start" spacing={6}>
      <BaseSkeleton w="48px" h="48px" />
      <VStack align="start" spacing={4} w="100%">
        <BaseSkeleton h="24px" w="80%" />
        <BaseSkeletonText noOfLines={3} spacing={2} w="100%" />
        <BaseSkeleton h="12px" w="60%" />
      </VStack>
    </VStack>
  </Box>
);

export const ProjectCardSkeleton = () => (
  <Box bg="gray.900" p={8} border="1px solid" borderColor="gray.700">
    <VStack align="start" spacing={6}>
      <VStack align="start" spacing={3} w="100%">
        <HStack spacing={2}>
          <BaseSkeleton w={1.5} h={1.5} borderRadius="full" />
          <BaseSkeleton h="10px" w="80px" />
        </HStack>
        <BaseSkeleton h="24px" w="90%" />
        <BaseSkeletonText noOfLines={3} spacing={2} w="100%" />
      </VStack>
      
      <HStack justify="space-between" w="100%">
        <BaseSkeleton h="12px" w="80px" />
        <BaseSkeleton h="10px" w="100px" />
      </HStack>
    </VStack>
  </Box>
);

export const ProjectsPageSkeleton = () => (
  <Box py={20} px={4}>
    <VStack spacing={20} maxW="5xl" mx="auto">
      {/* Hero Section Skeleton */}
      <VStack spacing={8} textAlign="center" w="100%">
        <BaseSkeleton h="60px" w="600px" maxW="100%" />
        <BaseSkeletonText 
          noOfLines={3} 
          spacing={2}
          maxW="4xl"
        />
      </VStack>

      {/* Featured Project Skeleton */}
      <Box bg="gray.900" p={12} border="1px solid" borderColor="gray.700" w="100%">
        <Grid templateColumns="1fr 1fr" gap={12} alignItems="center">
          <VStack align="start" spacing={6}>
            <VStack align="start" spacing={3} w="100%">
              <HStack spacing={3}>
                <BaseSkeleton w={2} h={2} borderRadius="full" />
                <BaseSkeleton h="12px" w="140px" />
              </HStack>
              <BaseSkeleton h="32px" w="80%" />
              <BaseSkeletonText noOfLines={3} spacing={2} w="100%" />
            </VStack>
            
            <VStack align="start" spacing={4} w="100%">
              <VStack align="start" spacing={2} w="100%">
                <BaseSkeleton h="12px" w="100px" />
                <HStack wrap="wrap" spacing={2}>
                  {[...Array(5)].map((_, i) => (
                    <BaseSkeleton key={i} h="24px" w="60px" />
                  ))}
                </HStack>
              </VStack>
              <BaseSkeleton h="32px" w="140px" borderRadius="md" />
            </VStack>
          </VStack>

          <BaseSkeleton h="300px" w="100%" />
        </Grid>
      </Box>

      {/* Project Categories Section */}
      <VStack spacing={12} w="100%">
        <BaseSkeleton h="48px" w="400px" maxW="100%" />
        
        <Grid templateColumns="repeat(3, 1fr)" gap={8}>
          <ProjectCategorySkeleton />
          <ProjectCategorySkeleton />
          <ProjectCategorySkeleton />
        </Grid>
      </VStack>

      {/* Recent Projects Section */}
      <VStack spacing={12} w="100%">
        <BaseSkeleton h="48px" w="300px" maxW="100%" />
        
        <Grid templateColumns="repeat(2, 1fr)" gap={8}>
          <ProjectCardSkeleton />
          <ProjectCardSkeleton />
          <ProjectCardSkeleton />
          <ProjectCardSkeleton />
        </Grid>
      </VStack>

      {/* CTA Section Skeleton */}
      <VStack spacing={8} textAlign="center" w="100%">
        <BaseSkeleton h="48px" w="400px" maxW="100%" />
        <BaseSkeletonText 
          noOfLines={3} 
          spacing={2}
          maxW="3xl"
        />
        <BaseSkeleton h="48px" w="160px" borderRadius="md" />
      </VStack>
    </VStack>
  </Box>
);